"use client";

import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, MessageSquare, Zap, Download } from "lucide-react";
import { gradient } from "./utils/constants";

const steps = [
  {
    icon: Upload,
    title: "Upload Your Images",
    description:
      "Securely upload your photos through our platform or share via cloud storage",
    step: "01",
  },
  {
    icon: MessageSquare,
    title: "Share Your Vision",
    description:
      "Tell us exactly what you need - our team will understand your requirements",
    step: "02",
  },
  {
    icon: Zap,
    title: "We Work Our Magic",
    description:
      "Our expert editors transform your images with precision and creativity",
    step: "03",
  },
  {
    icon: Download,
    title: "Download & Enjoy",
    description:
      "Receive your professionally edited images, usually within 24 hours",
    step: "04",
  },
];

export function Process() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Getting professional photo editing is simple. Follow these four easy
            steps to transform your images into stunning visuals.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Card className="h-full hover:shadow-xl transition-all duration-300 group border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="relative mb-6">
                    <div
                      className={`w-20 h-20 ${gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <step.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-purple-200">
                      <span
                        className={`text-sm font-bold text-primary ${gradient} bg-clip-text text-transparent`}
                      >
                        {step.step}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div
                  className={`hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 ${gradient} transform -translate-y-1/2 z-10`}
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
